import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { DisplayNameEdit } from './DisplayNameEdit';
import { LocaleSwitcher } from './LocaleSwitcher';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

const HIDE_HEADER = new Set(['/play']);

function hideHeader(pathname: string) {
  if (HIDE_HEADER.has(pathname)) return true;
  if (/^\/archives\/[^/]+\/play$/.test(pathname)) return true;
  return false;
}

function navClass(pathname: string, match: string | RegExp) {
  const active = typeof match === 'string'
    ? pathname === match || (match !== '/' && pathname.startsWith(match))
    : match.test(pathname);
  return active ? 'active' : undefined;
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`site-header-menu-icon${open ? ' is-open' : ''}`}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path className="site-header-menu-bar" d="M4 7h16" />
      <path className="site-header-menu-bar" d="M4 12h16" />
      <path className="site-header-menu-bar" d="M4 17h16" />
    </svg>
  );
}

function NavIcon({ d }: { d: string }) {
  return (
    <svg className="site-header-nav-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICONS = {
  play: 'M8 5v14l11-7z',
  trophy: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM5 6H3v2a3 3 0 0 0 3 3M19 6h2v2a3 3 0 0 1-3 3',
  archive: 'M3 7h18M5 7v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7M10 11h4',
  star: 'M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.8 6.2 21l1.1-6.5L2.6 9.8l6.5-.9L12 3z',
  discover: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 8v4l3 2',
  how: 'M9.5 9a2.5 2.5 0 1 1 3.8 2.1c-.8.5-1.3 1.1-1.3 2V14M12 17h.01',
  friends: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  premium: 'M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z',
  admin: 'M12 3l8 4v5c0 5-3.5 8.5-8 9.5C7.5 20.5 4 17 4 12V7l8-4z',
};

type DropdownId = 'leaderboards' | 'history';

function NavDropdown({
  id,
  openId,
  onOpen,
  onScheduleClose,
  toggleTo,
  toggleLabel,
  toggleActive,
  links,
}: {
  id: DropdownId;
  openId: DropdownId | null;
  onOpen: (id: DropdownId) => void;
  onScheduleClose: () => void;
  toggleTo: string;
  toggleLabel: string;
  toggleActive: boolean;
  links: { to: string; label: string }[];
}) {
  return (
    <div
      className={`site-header-dropdown${openId === id ? ' is-open' : ''}`}
      onMouseEnter={() => onOpen(id)}
      onMouseLeave={onScheduleClose}
    >
      <Link
        to={toggleTo}
        className={`site-header-dropdown-toggle${toggleActive ? ' active' : ''}`}
        aria-haspopup="true"
        aria-expanded={openId === id}
      >
        {toggleLabel}
      </Link>
      <div className="site-header-dropdown-menu">
        {links.map((link) => (
          <Link key={link.to} to={link.to}>{link.label}</Link>
        ))}
      </div>
    </div>
  );
}

function MobileNavLink({
  to,
  label,
  icon,
  className,
  onClick,
  primary,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  className?: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`${primary ? 'site-header-nav-primary ' : ''}${className || ''}`.trim()}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
    </Link>
  );
}

export function SiteHeader() {
  const { pathname } = useLocation();
  const { user, guest, loading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownId | null>(null);
  const closeDropdownTimer = useRef<number | null>(null);
  const hidden = hideHeader(pathname);

  const lbActive = pathname.startsWith('/leaderboard');
  const closeMenu = () => setMenuOpen(false);

  const openDropdownNow = (id: DropdownId) => {
    if (closeDropdownTimer.current) window.clearTimeout(closeDropdownTimer.current);
    setOpenDropdown(id);
  };

  const scheduleCloseDropdown = () => {
    if (closeDropdownTimer.current) window.clearTimeout(closeDropdownTimer.current);
    closeDropdownTimer.current = window.setTimeout(() => setOpenDropdown(null), 220);
  };

  useEffect(() => {
    setMenuOpen(false);
    setOpenDropdown(null);
  }, [pathname]);

  useEffect(() => {
    if (hidden || !menuOpen) {
      document.body.classList.remove('nav-open');
      return;
    }
    document.body.classList.add('nav-open');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('nav-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, hidden]);

  if (hidden) return null;

  const leaderboardLinks = [
    { to: '/leaderboard', label: t('leaderboard.periodToday') },
    { to: '/leaderboard/semaine', label: t('leaderboard.periodWeek') },
    { to: '/leaderboard/mois', label: t('leaderboard.periodMonth') },
    { to: '/leaderboard/tous-les-temps', label: t('leaderboard.periodAllTime') },
  ];

  const historyActive = pathname.startsWith('/archives') || pathname.startsWith('/top');

  const historyLinks = [
    { to: '/archives', label: t('archives.title') },
    { to: '/top', label: t('nav.top') },
  ];

  const accountBlock = (onNavigate?: () => void) => (
    <>
      <LocaleSwitcher />
      {loading ? (
        <span className="site-header-muted">…</span>
      ) : user ? (
        <div className="site-header-account-user">
          <DisplayNameEdit />
          {user.isGuest && (
            <span className="site-header-guest-badge">{t('leaderboard.guestBadge')}</span>
          )}
        </div>
      ) : (
        <Link to="/login" className="site-header-login" onClick={onNavigate}>
          {t('landing.login')}
        </Link>
      )}
    </>
  );

  const mobileMenu = menuOpen
    ? createPortal(
      <div className="site-header-mobile-overlay is-open" role="dialog" aria-modal="true" aria-label={t('nav.main')}>
        <button
          type="button"
          className="site-header-backdrop"
          onClick={closeMenu}
        >
          <span className="sr-only">{t('nav.closeMenu')}</span>
        </button>

        <nav id="site-header-nav-mobile" className="site-header-nav site-header-nav--mobile">
          <MobileNavLink
            to="/play"
            label={t('nav.daily')}
            icon={<NavIcon d={ICONS.play} />}
            className={navClass(pathname, '/play')}
            onClick={closeMenu}
            primary
          />

          <div className="site-header-nav-group">
            <span className="site-header-nav-label">{t('nav.playSection')}</span>
            <MobileNavLink
              to="/leaderboard"
              label={t('nav.leaderboards')}
              icon={<NavIcon d={ICONS.trophy} />}
              className={navClass(pathname, '/leaderboard')}
              onClick={closeMenu}
            />
            <MobileNavLink
              to="/archives"
              label={t('archives.title')}
              icon={<NavIcon d={ICONS.archive} />}
              className={navClass(pathname, '/archives')}
              onClick={closeMenu}
            />
            <MobileNavLink
              to="/top"
              label={t('nav.top')}
              icon={<NavIcon d={ICONS.star} />}
              className={navClass(pathname, '/top')}
              onClick={closeMenu}
            />
          </div>

          <div className="site-header-nav-group">
            <span className="site-header-nav-label">{t('nav.infoSection')}</span>
            <MobileNavLink
              to="/decouvrir"
              label={t('nav.discover')}
              icon={<NavIcon d={ICONS.discover} />}
              className={navClass(pathname, '/decouvrir')}
              onClick={closeMenu}
            />
            <MobileNavLink
              to="/comment-jouer"
              label={t('nav.howToPlay')}
              icon={<NavIcon d={ICONS.how} />}
              className={navClass(pathname, '/comment-jouer')}
              onClick={closeMenu}
            />
          </div>

          {user && !guest && (
            <MobileNavLink
              to="/friends"
              label={t('nav.friends')}
              icon={<NavIcon d={ICONS.friends} />}
              className={navClass(pathname, '/friends')}
              onClick={closeMenu}
            />
          )}

          {!user?.isPremium && (
            <MobileNavLink
              to="/premium"
              label={t('premium.title')}
              icon={<NavIcon d={ICONS.premium} />}
              className={navClass(pathname, '/premium')}
              onClick={closeMenu}
            />
          )}
          {user?.isAdmin && (
            <MobileNavLink
              to="/admin"
              label={t('nav.admin')}
              icon={<NavIcon d={ICONS.admin} />}
              className={navClass(pathname, '/admin')}
              onClick={closeMenu}
            />
          )}

          <div className="site-header-mobile-foot">
            {accountBlock(closeMenu)}
          </div>
        </nav>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <header className={`site-header${menuOpen ? ' is-menu-open' : ''}`}>
        <Link to="/" className="site-header-logo" onClick={closeMenu}>
          Dezoom
        </Link>

        <nav className="site-header-nav site-header-nav--desktop" aria-label={t('nav.main')}>
          <Link to="/play" className={navClass(pathname, '/play')}>{t('nav.daily')}</Link>

          <NavDropdown
            id="leaderboards"
            openId={openDropdown}
            onOpen={openDropdownNow}
            onScheduleClose={scheduleCloseDropdown}
            toggleTo="/leaderboard"
            toggleLabel={t('nav.leaderboards')}
            toggleActive={lbActive}
            links={leaderboardLinks}
          />

          <NavDropdown
            id="history"
            openId={openDropdown}
            onOpen={openDropdownNow}
            onScheduleClose={scheduleCloseDropdown}
            toggleTo="/archives"
            toggleLabel={t('nav.history')}
            toggleActive={historyActive}
            links={historyLinks}
          />

          <Link to="/decouvrir" className={navClass(pathname, '/decouvrir')}>
            {t('nav.discover')}
          </Link>
          <Link to="/comment-jouer" className={navClass(pathname, '/comment-jouer')}>
            {t('nav.howToPlay')}
          </Link>

          {user && !guest && (
            <Link to="/friends" className={navClass(pathname, '/friends')}>
              {t('nav.friends')}
            </Link>
          )}

          {!user?.isPremium && (
            <Link to="/premium" className={navClass(pathname, '/premium')}>
              {t('premium.title')}
            </Link>
          )}
          {user?.isAdmin && (
            <Link to="/admin" className={navClass(pathname, '/admin')}>
              {t('nav.admin')}
            </Link>
          )}
        </nav>

        <div className="site-header-account site-header-account--desktop">
          {accountBlock()}
        </div>

        <button
          type="button"
          className="site-header-menu-btn"
          aria-expanded={menuOpen}
          aria-controls="site-header-nav-mobile"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="sr-only">
            {menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          </span>
          <MenuIcon open={menuOpen} />
        </button>
      </header>

      {mobileMenu}
    </>
  );
}
